import { useEffect, useRef } from "react";
import { getPlatform } from "utils/config";
import { useFrontEndLoggerMutation } from "redux/reduxAPI";

const pushUtmToDataLayer = (sources) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(sources);
};

/** Separate dataLayer push for phone (not merged with UTMs). */
export function pushPhoneNumberToDataLayer(phoneNumber) {
  if (phoneNumber == null || phoneNumber === "") {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ phone_number: String(phoneNumber) });
}

/** UTM / device_platform only — no phone (see `usePushPhoneNumberToDataLayer` in router). */
export const useUtmTracker = () => {
  const params = new URLSearchParams(window.location.search);
  const [logError] = useFrontEndLoggerMutation();

  useEffect(() => {
    // Persist utm_source under a dedicated key that survives the OTP login flow
    // (which wipes `creatorSource` after attributing it to the BE). Features
    // that need the original utm post-login (e.g. CXB-3342 brand-campaign
    // interest form) read from this key.
    const urlUtmSource = params.get("utm_source");
    if (urlUtmSource) {
      localStorage.setItem("utmSource", urlUtmSource);
    }

    let creatorSource = {};
    try {
      creatorSource = JSON.parse(localStorage.getItem("creatorSource"));
    } catch (error) {
      // do nothing
    }
    if (creatorSource && Object.keys(creatorSource).length > 0) {
      pushUtmToDataLayer(creatorSource);
      return;
    }
    const sources = {};
    const utm_tags = ["utm_source", "utm_campaign", "utm_medium"];
    for (const tag of utm_tags) {
      const tagValue = params.get(tag);
      if (tagValue) {
        sources[tag] = tagValue;
      }
    }
    sources["device_platform"] = getPlatform(logError);
    localStorage.setItem("creatorSource", JSON.stringify(sources));
    pushUtmToDataLayer(sources);
  }, [logError]);
};

/** Call next to `useUtmTracker()` — pushes phone to dataLayer separately (not merged with UTMs). */
export const usePushPhoneNumberToDataLayer = (phoneNumber) => {
  const lastPhonePushedRef = useRef(null);

  useEffect(() => {
    if (phoneNumber == null || phoneNumber === "") {
      lastPhonePushedRef.current = null;
      return;
    }
    const normalized = String(phoneNumber);
    if (lastPhonePushedRef.current === normalized) {
      return;
    }
    lastPhonePushedRef.current = normalized;
    pushPhoneNumberToDataLayer(normalized);
  }, [phoneNumber]);
};
